import { useEffect, useRef, useState } from 'react'

/**
 * The header's "⋯ More" menu (figure S3).
 *
 * The figure gives the invention header one primary action — Upload — and puts
 * everything else behind this menu. It exists so that the actions a page really
 * has (sharing it, re-running its analysis) are not deleted to make the header
 * look like the picture: a control that is removed is a capability removed, and
 * the figure's own "⋯" is where they belong.
 */
export interface HeaderAction {
  label: string
  onClick: () => void
  disabled?: boolean
  /** Rendered in red — a destructive action. */
  danger?: boolean
}

export interface HeaderActionMenuProps {
  actions: HeaderAction[]
  label?: string
}

export default function HeaderActionMenu({ actions, label = '⋯ More' }: HeaderActionMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  if (actions.length === 0) return null

  return (
    <div className="relative" ref={ref} data-part="HeaderActionMenu">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="rounded-lg border border-sky-200 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-sky-50"
      >
        {label}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-20 mt-1 min-w-[13rem] rounded-lg border border-sky-200 bg-white py-1 shadow-xl">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              disabled={action.disabled}
              onClick={() => { action.onClick(); setOpen(false) }}
              className={`block w-full px-3 py-2 text-left text-sm disabled:opacity-50 ${
                action.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-600 hover:bg-sky-50'
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
